/** Глоссарий терминов юнит-экономики для тултипов в UI */

import { CAMPAIGN_GLOSSARY } from "./campaignGlossary";

export type GlossaryTerm = {
    /** Короткое имя, как на экране */
    label: string;
    /** Расшифровка аббревиатуры / англицизма */
    expand: string;
    /** Простое объяснение по-русски */
    meaning: string;
    /** Как считается — словами */
    formula?: string;
    /** Пример расчёта на числах */
    example?: string;
    /** Ссылка на русскую Википедию (если есть статья) */
    wikiRu?: string;
};

export const UE_GLOSSARY = {
    // Термины карточки рекламной кампании — с формулой и примером.
    ...CAMPAIGN_GLOSSARY,
    takeRate: {
        label: "Take Rate",
        expand: "доля комиссии платформы (англ. take rate)",
        meaning:
            "Какой процент от оборота (GMV) остаётся площадке как комиссия. У нас: комиссии ордеров ÷ GMV.",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%9A%D0%BE%D0%BC%D0%B8%D1%81%D1%81%D0%B8%D1%8F",
    },
    gmv: {
        label: "GMV",
        expand: "Gross Merchandise Volume — общий объём оборота товаров",
        meaning:
            "Сумма цен проданных товаров за период (оборот маркетплейса), без вычета комиссий площадки.",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%9E%D0%B1%D1%89%D0%B8%D0%B9_%D0%BE%D0%B1%D1%8A%D1%91%D0%BC_%D0%BE%D0%B1%D0%BE%D1%80%D0%BE%D1%82%D0%B0_%D1%82%D0%BE%D0%B2%D0%B0%D1%80%D0%BE%D0%B2",
    },
    waterfall: {
        label: "Waterfall",
        expand: "каскад / «водопад» структуры прибыли",
        meaning:
            "Разбивка итоговой прибыли по источникам: ордера, депозиты, выводы, корректировки.",
    },
    arpu: {
        label: "ARPU",
        expand: "Average Revenue Per User — средняя выручка на пользователя",
        meaning:
            "Прибыль платформы за период, делённая на число активных пользователей.",
        wikiRu: "https://ru.wikipedia.org/wiki/ARPU",
    },
    arppu: {
        label: "ARPPU",
        expand: "Average Revenue Per Paying User — средняя выручка на платящего",
        meaning:
            "Прибыль платформы за период, делённая только на пользователей с депозитом или сделкой.",
        wikiRu: "https://ru.wikipedia.org/wiki/ARPPU",
    },
    aov: {
        label: "AOV",
        expand: "Average Order Value — средний чек / средняя стоимость заказа",
        meaning: "Средний GMV одного ордера: оборот ÷ число ордеров.",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%A1%D1%80%D0%B5%D0%B4%D0%BD%D0%B8%D0%B9_%D1%87%D0%B5%D0%BA",
    },
    fee: {
        label: "Fee",
        expand: "комиссия / сбор",
        meaning: "Плата площадки с сделки (торговая комиссия ордера).",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%9A%D0%BE%D0%BC%D0%B8%D1%81%D1%81%D0%B8%D1%8F",
    },
    ltv: {
        label: "LTV",
        expand: "Lifetime Value — пожизненная ценность клиента",
        meaning:
            "Сколько прибыли (contribution) приносит пользователь. У нас за выбранный период, не за всю жизнь аккаунта.",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%9F%D0%BE%D0%B6%D0%B8%D0%B7%D0%BD%D0%B5%D0%BD%D0%BD%D0%B0%D1%8F_%D1%86%D0%B5%D0%BD%D0%BD%D0%BE%D1%81%D1%82%D1%8C_%D0%BA%D0%BB%D0%B8%D0%B5%D0%BD%D1%82%D0%B0",
    },
    contribution: {
        label: "Contribution",
        expand: "вклад в прибыль / маржинальный вклад",
        meaning:
            "Прибыль, которую пользователь или поток «вносит» платформе: комиссии и OurBenefit платежей.",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%9C%D0%B0%D1%80%D0%B6%D0%B8%D0%BD%D0%B0%D0%BB%D1%8C%D0%BD%D0%B0%D1%8F_%D0%BF%D1%80%D0%B8%D0%B1%D1%8B%D0%BB%D1%8C",
    },
    ttfd: {
        label: "TTFD",
        expand: "Time To First Deposit — время до первого депозита",
        meaning: "Сколько часов/дней прошло от регистрации до первого успешного пополнения.",
    },
    ttft: {
        label: "TTFT",
        expand: "Time To First Trade — время до первой сделки",
        meaning: "Сколько часов/дней прошло от регистрации до первого завершённого ордера.",
    },
    retention: {
        label: "Retention",
        expand: "удержание клиентов",
        meaning:
            "Доля пользователей когорты, которые остаются активными через N дней после регистрации (D1/D7/D30).",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%A3%D0%B4%D0%B5%D1%80%D0%B6%D0%B0%D0%BD%D0%B8%D0%B5_%D0%BA%D0%BB%D0%B8%D0%B5%D0%BD%D1%82%D0%BE%D0%B2",
    },
    churn: {
        label: "Churn",
        expand: "отток клиентов (churn rate)",
        meaning:
            "Доля пользователей, которые перестали быть активными (у нас — нет активности 30 дней).",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%9E%D1%82%D1%82%D0%BE%D0%BA_%D0%BA%D0%BB%D0%B8%D0%B5%D0%BD%D1%82%D0%BE%D0%B2",
    },
    cohort: {
        label: "Когорта",
        expand: "cohort — группа пользователей с общим признаком",
        meaning: "Например, все, кто зарегистрировался в одном месяце — для сравнения retention.",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%9A%D0%BE%D0%B3%D0%BE%D1%80%D1%82%D0%BD%D1%8B%D0%B9_%D0%B0%D0%BD%D0%B0%D0%BB%D0%B8%D0%B7",
    },
    d1: {
        label: "D1",
        expand: "Day 1 retention",
        meaning: "Доля зарегистрированных, у кого активность была не раньше чем через 1 день после регистрации.",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%A3%D0%B4%D0%B5%D1%80%D0%B6%D0%B0%D0%BD%D0%B8%D0%B5_%D0%BA%D0%BB%D0%B8%D0%B5%D0%BD%D1%82%D0%BE%D0%B2",
    },
    d7: {
        label: "D7",
        expand: "Day 7 retention",
        meaning: "Удержание на 7-й день после регистрации.",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%A3%D0%B4%D0%B5%D1%80%D0%B6%D0%B0%D0%BD%D0%B8%D0%B5_%D0%BA%D0%BB%D0%B8%D0%B5%D0%BD%D1%82%D0%BE%D0%B2",
    },
    d30: {
        label: "D30",
        expand: "Day 30 retention",
        meaning: "Удержание на 30-й день после регистрации.",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%A3%D0%B4%D0%B5%D1%80%D0%B6%D0%B0%D0%BD%D0%B8%D0%B5_%D0%BA%D0%BB%D0%B8%D0%B5%D0%BD%D1%82%D0%BE%D0%B2",
    },
    pareto: {
        label: "Pareto",
        expand: "принцип Парето (80/20)",
        meaning:
            "Часто небольшая доля пользователей даёт большую долю прибыли. Смотрим top 1% / 5% / 10%.",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%97%D0%B0%D0%BA%D0%BE%D0%BD_%D0%9F%D0%B0%D1%80%D0%B5%D1%82%D0%BE",
    },
    float: {
        label: "Float",
        expand: "денежный «зазор» / подушка ликвидности",
        meaning:
            "У нас: балансы у платёжных провайдеров (Tome + GMoney) минус обязательства сайта (Site).",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%9B%D0%B8%D0%BA%D0%B2%D0%B8%D0%B4%D0%BD%D0%BE%D1%81%D1%82%D1%8C",
    },
    netFlow: {
        label: "Net flow",
        expand: "чистый денежный поток",
            meaning: "Депозиты минус выводы за период: сколько денег нетто пришло на балансы пользователей.",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%94%D0%B5%D0%BD%D0%B5%D0%B6%D0%BD%D1%8B%D0%B9_%D0%BF%D0%BE%D1%82%D0%BE%D0%BA",
    },
    chargeback: {
        label: "Chargeback",
        expand: "чарджбек — отмена платежа банком/платёжкой",
        meaning:
            "Покупатель оспорил оплату; деньги могут списать обратно. Это потери и риск для площадки.",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%9E%D1%82%D0%BC%D0%B5%D0%BD%D0%B0_%D0%BF%D0%BB%D0%B0%D1%82%D0%B5%D0%B6%D0%B0",
    },
    refund: {
        label: "Refund",
        expand: "возврат средств",
        meaning: "Деньги вернули пользователю (рефанд депозита/операции).",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%92%D0%BE%D0%B7%D0%B2%D1%80%D0%B0%D1%82_%D1%82%D0%BE%D0%B2%D0%B0%D1%80%D0%B0",
    },
    funnel: {
        label: "Воронка",
        expand: "funnel — воронка конверсии",
        meaning: "Сколько людей проходит каждый шаг: регистрация → депозит → сделка.",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%92%D0%BE%D1%80%D0%BE%D0%BD%D0%BA%D0%B0_%D0%BF%D1%80%D0%BE%D0%B4%D0%B0%D0%B6",
    },
    median: {
        label: "Медиана",
        expand: "median — срединное значение",
        meaning: "Значение «посередине» списка: типичнее среднего, меньше зависит от выбросов.",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%9C%D0%B5%D0%B4%D0%B8%D0%B0%D0%BD%D0%B0_(%D1%81%D1%82%D0%B0%D1%82%D0%B8%D1%81%D1%82%D0%B8%D0%BA%D0%B0)",
    },
    p90: {
        label: "p90",
        expand: "90-й перцентиль",
        meaning: "90% пользователей быстрее этого значения, 10% — медленнее (хвост «долгих»).",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%9A%D0%B2%D0%B0%D0%BD%D1%82%D0%B8%D0%BB%D1%8C",
    },
    repeat: {
        label: "Repeat rate",
        expand: "доля повторных действий",
        meaning: "Какая доля пользователей сделала ≥2 депозита или ≥2 ордера за период.",
    },
    seller: {
        label: "Seller",
        expand: "продавец",
        meaning: "Пользователь, который продаёт скины на площадке.",
    },
    buyer: {
        label: "Buyer",
        expand: "покупатель",
        meaning: "Пользователь, который покупает скины на площадке.",
    },
    hybrid: {
        label: "Hybrid",
        expand: "гибрид — и покупатель, и продавец",
        meaning:
            "Пользователь, у которого в окне есть и покупки, и продажи. Не пересекается с buyer-only и seller-only.",
    },
    unitEconomics: {
        label: "Юнит-экономика",
        expand: "unit economics — экономика на единицу",
        meaning:
            "Считаем прибыльность «на одного пользователя / одну сделку», а не только общий P&L компании.",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%9F%D0%BE%D0%B6%D0%B8%D0%B7%D0%BD%D0%B5%D0%BD%D0%BD%D0%B0%D1%8F_%D1%86%D0%B5%D0%BD%D0%BD%D0%BE%D1%81%D1%82%D1%8C_%D0%BA%D0%BB%D0%B8%D0%B5%D0%BD%D1%82%D0%B0",
    },
    paid: {
        label: "Paid",
        expand: "успешно оплаченный",
        meaning: "Статус депозита: счёт оплачен, средства зачислены.",
    },
    ourBenefit: {
        label: "OurBenefit",
        expand: "наша выгода с платежа",
        meaning:
            "Маржа платформы с депозита/вывода после комиссии провайдера (то, что реально зарабатываем на платеже).",
    },
    cac: {
        label: "CAC",
        expand: "Customer Acquisition Cost — стоимость привлечения клиента",
        meaning:
            "Сколько в среднем стоит один привлечённый пользователь: spend кампании ÷ число first-touch.",
        wikiRu: "https://ru.wikipedia.org/wiki/%D0%A1%D1%82%D0%BE%D0%B8%D0%BC%D0%BE%D1%81%D1%82%D1%8C_%D0%BF%D1%80%D0%B8%D0%B2%D0%BB%D0%B5%D1%87%D0%B5%D0%BD%D0%B8%D1%8F_%D0%BA%D0%BB%D0%B8%D0%B5%D0%BD%D1%82%D0%B0",
    },
    romi: {
        label: "ROMI",
        expand: "Return On Marketing Investment — возврат маркетинговых инвестиций",
        meaning:
            "(Contribution − затраты на промо − Spend) / Spend. 0 — выход в ноль; отрицательное — кампания пока в минусе.",
    },
    roas: {
        label: "ROAS",
        expand: "Return On Ad Spend — возврат рекламных затрат",
        meaning:
            "Во сколько раз contribution когорты за вычетом затрат на промо покрывает spend: (contribution − промо) ÷ spend. 1× — выход в ноль.",
    },
    payback: {
        label: "Payback",
        expand: "срок окупаемости",
        meaning:
            "Сколько дней от создания кампании прошло, пока кумулятивный contribution когорты за вычетом затрат на промо не покрыл spend. «—» — spend не задан или ещё не окупилась.",
    },
    costPerActivation: {
        label: "Cost / activation",
        expand: "стоимость одной активации",
        meaning:
            "Spend кампании ÷ число Activated (первый Paid-депозит). «—» — spend не задан или активаций нет.",
    },
    costPerTrade: {
        label: "Cost / trade",
        expand: "стоимость первой сделки",
        meaning:
            "Spend кампании ÷ число пользователей с первой покупкой (buyer). «—» — spend не задан или сделок нет.",
    },
    ltvCac: {
        label: "LTV/CAC",
        expand: "отношение lifetime value к стоимости привлечения",
        meaning:
            "ARPU когорты (contribution / Registered) ÷ CAC. Выше 1 — средний LTV покрывает привлечение; «—» если нет spend или регистраций.",
    },
    spendShare: {
        label: "Spend / Contrib",
        expand: "доля рекламных затрат в contribution",
        meaning:
            "Spend ÷ contribution когорты. 100% — spend равен прибыли; выше 100% — кампания пока в минусе. «—» если spend не задан или contribution = 0.",
    },
    netContribution: {
        label: "Чистый Contribution",
        expand: "contribution после рекламных затрат и промо",
        meaning:
            "Contribution когорты минус Spend и затраты на промо-бонусы её пользователей. Отрицательное — реклама ещё не отбита. «—» если spend не задан.",
    },
    promoCost: {
        label: "Затраты на промо",
        expand: "стоимость бонусов по промокодам",
        meaning:
            "Сумма бонусов, которые получили пользователи когорты: недобор комиссии продавцу, скидки покупателю и бонусы к депозиту. Contribution в отчётах кампании считается по обычной комиссии, а затраты вычитаются отдельно — без двойного учёта.",
    },
    spendShareOfGmv: {
        label: "Spend / GMV",
        expand: "доля рекламных расходов в обороте",
        meaning:
            "Spend ÷ GMV ордеров когорты (каждый ордер один раз, если buyer или seller из first-touch). «—» если spend не задан или GMV = 0.",
    },
    gmvBuy: {
        label: "GMV покупок",
        expand: "оборот покупок когорты",
        meaning:
            "Сумма цен ордеров, где пользователь когорты — покупатель (buyer-only + hybrid).",
    },
    gmvSell: {
        label: "GMV продаж",
        expand: "оборот продаж когорты",
        meaning:
            "Сумма цен ордеров, где пользователь когорты — продавец (seller-only + hybrid).",
    },
    repeatTrade: {
        label: "Repeat trade",
        expand: "доля когорты с повторными покупками",
        meaning:
            "Пользователи с ≥2 покупками ÷ вся когорта first-touch, не только среди тех, кто уже купил.",
    },
} as const satisfies Record<string, GlossaryTerm>;

export type GlossaryKey = keyof typeof UE_GLOSSARY;
